import "./index.css";
import React, {Suspense, useCallback, useEffect, useMemo, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useSearchParams} from "react-router-dom";
import {Loader2} from "lucide-react";

//Constant config variable imports
import {INTERACTION_TYPE, MESSAGE_TYPES} from "/src/utils/constant.js"

// API imports
import {
    getBrandingDetailsAPI,
    getDownloadIconAPI,
    getInterfaceSession,
    getProgressStateAPI,
    getReplaySession,
} from "./services/api/api.js";
import initializeSocket from "./socket.js";
import {axiosManager} from "./utils/axiosmanager.js";

// Redux actions and selectors
import {getUserInitialize} from "./slices/actions.js";
import {
    setAppLoader,
    setBlockError,
    setBrandIcon,
    setBrandingData,
    setButtonInputHint,
    setComponentData,
    setErrorToastMessage, setIsHalt, setIsTerminate,
    setLoader, setProgressLoader,
    setProgressState,
    setReplayComponents,
    setSessionData,
    setSocket,
    setStartInterfaceBlockID,
    setTraceData,
    setIsExplicitRetrying
} from "./slices/slices.js";
import { toast } from "sonner";

// Components
const ProductionItem = React.lazy(() => import("./views/pages/ProductItem/ProductItem.jsx"));
const Error = React.lazy(() => import("./views/components/ErrorComponent/ErrorComponent.jsx"));

const LoadingFallback = () => (
    <div className="flex h-full flex-col items-center justify-center">
        <Loader2 className="animate-spin"/>
        <p>Please wait...</p>
    </div>
);

const LoadingScreen = () => (
    <div className="flex h-full flex-col items-center justify-center">
        <Loader2 className="animate-spin"/>
        <p>Please wait...</p>
    </div>
);

function App() {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();

    const isComponentData = useRef(false);

    // Redux selectors
    const {
        initialized,
        components,
        interfaceSession,
        appLoader,
        socket,
        step,
        traceData,
        isTerminate,
    } = useSelector((state) => state.userSlice);

    const {token, error, loader} = useSelector((state) => state.credentialSlice);

    const count = useRef(null);

    //Handle pending state counter if socket takes too much time to get response
    const responseSecond = useRef(0);
    const responseTimerRef = useRef();

    function getHandlePending() {
        responseTimerRef.current = setInterval(() => {
            responseSecond.current += 1;
            if (responseSecond.current == 60) {
                responseSecond.current = 0;
                responseTimerRef.current = null;
                clearInterval(responseTimerRef.current);
                if (interfaceSession?.redirect_url) {
                    const separator = interfaceSession.redirect_url.includes('?') ? '&' : '/?';
                    const redirectUrl = `${interfaceSession.redirect_url}${separator}status=pending`;
                    setTimeout(() => {
                        window.open(redirectUrl, "_self");
                    }, 300);
                }

            }
        }, 1000);
    }


    useEffect(() => {
        if (appLoader) {
            getHandlePending();
            return;
        }
        responseSecond.current = 0;
        clearInterval(responseTimerRef.current);
        responseTimerRef.current = null;

    }, [appLoader, components]);

    // Initialize app with signature
    useEffect(() => {
        const signature = searchParams.get("signature");
        if (signature && !initialized && !token) {
            dispatch(getUserInitialize(signature));
        }

        return () => {
            if (socket) socket.close();
        };
    }, [dispatch, initialized, searchParams, socket]);

    // Socket event handler
    const handleSocketEvent = useCallback((eventName, ...args) => {
        if (eventName !== "INTERFACE_OUTGOING_MESSAGE") return;

        const [eventData] = args;
        const {data: {message, interface_block_id} = {}} = eventData || {};

        dispatch(setLoader(false));

        if (!isComponentData.current) {
            isComponentData.current = true;
        }


        if (message?.message_type === 'HALT') {
            dispatch(setIsHalt(true));
            dispatch(setAppLoader(false));
            dispatch(setIsExplicitRetrying(false));
            dispatch(setIsTerminate(false));
            return;
        }

        if (message?.message_type === "ELEMENT" && message?.element_type === "SUCCESS_MESSAGE") {
            toast.success(message?.heading || "Success", {
                description: message?.text,
            });
            return;
        }

        if (message?.message_type === "ELEMENT" && message?.element_type === "FAILURE_MESSAGE") {
            toast.error(message?.heading || "Error", {
                description: message?.text,
            });
            return;
        }

        // Skip processing for specific block types
        if (
            message?.block_type === "ternary_router_block" ||
            message?.block_type === "parse_date_time_block" ||
            message?.element_type === "WAIT_MESSAGE" || message?.message_type === 'HALT'
        ) {
            return;
        }


        //Handle error toast message
        if (message?.element_type == 'ERROR_TOAST_MESSAGE') {
            dispatch(setErrorToastMessage(message?.text));
            return;
        }

        // Handle terminal states
        // Handle trace messages
        if (message?.message_type === MESSAGE_TYPES.TRACE) {
            const blockStatus = message?.block_current_status;
            const blockType = message?.block_type || "";

            const isErrorStatus = blockStatus === "FAILED" || blockStatus === "DEAD";
            const isExcludedBlockType = blockType.includes("_widget_block") || blockType.includes("on_interface_");

            if (isErrorStatus && !isExcludedBlockType) {
                dispatch(setAppLoader(false));
                dispatch(setIsExplicitRetrying(false));
                dispatch(setBlockError(message));
            }

            dispatch(setTraceData(args));
        }

        if (
            message?.message_type === MESSAGE_TYPES.TERMINATE
        ) {
            dispatch(setIsTerminate(true));
            dispatch(setAppLoader(false));
            dispatch(setIsExplicitRetrying(false));
            dispatch(setIsHalt(false));
        }

        //Handle Heartbeat
        if (message?.message_type === MESSAGE_TYPES.HINT && message?.interaction_type === INTERACTION_TYPE.HEARTBEAT) {
            socket.emit('interface_interaction', {
                interface_block_id: interface_block_id,
                interaction_type: message?.interaction_type
            })
        }


        // Handle hint messages for Button_Input
        if (message?.message_type === MESSAGE_TYPES.HINT && message?.interaction_type === INTERACTION_TYPE.BUTTON_INPUT && message?.ui?.screen?.definition?.wizard_type === 'AADHAAR_OKYC') {
            dispatch(setButtonInputHint(args[0]));
            return;
        }

        if (message?.message_type === MESSAGE_TYPES.HINT &&
            message?.interaction_type === "START_BUTTON") {
            dispatch(setAppLoader(true));
            socket.emit('interface_interaction', {
                interaction_type: message?.interaction_type,
                interface_block_id: interface_block_id
            })
        }

        if (
            message?.message_type !== MESSAGE_TYPES.TRACE &&
            message?.message_type !== MESSAGE_TYPES.TERMINATE &&
            !(
                message?.message_type === MESSAGE_TYPES.HINT &&
                message?.interaction_type === "START_BUTTON"
            )
        ) {
            dispatch(setComponentData(args));
            dispatch(setAppLoader(false));
            dispatch(setIsExplicitRetrying(false));
        }


    }, [dispatch, socket]);

    // Initialize socket connection
    useEffect(() => {
        if (token && !socket) {
            const newSocket = initializeSocket(token);
            dispatch(setSocket(newSocket));
        }
    }, [token, socket, dispatch]);

    // Setup socket listeners
    useEffect(() => {
        if (!socket) return;
        // socket.close();
        socket.onAny(handleSocketEvent);
        socket.on("disconnect", () => {
        });

    }, [socket]);

    // Initialize API data
    useEffect(() => {
        if (!token) return;

        axiosManager.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const fetchInitialData = async () => {
            try {
                const [sessionData, brandingDetails, brandIcon] = await Promise.allSettled([

                    getInterfaceSession(),
                    getBrandingDetailsAPI(),
                    getDownloadIconAPI(),
                ]);

                // dispatch(setProgressState(progressState.value.data?.progress_state?.block_states));
                dispatch(setSessionData(sessionData.value.data));
                dispatch(setBrandingData(brandingDetails.value.data));

                // Handle brand icon
                const blob = new Blob([brandIcon.value.data], {type: "image/x-icon"});
                const objectURL = URL.createObjectURL(blob);
                dispatch(setBrandIcon(objectURL));
                dispatch(setProgressLoader(true));

            } catch (error) {
                console.error("Error fetching initial data:", error);
            } finally {
                dispatch(setLoader(false));
            }
        };

        fetchInitialData();
    }, [token, dispatch]);

    // Handle replay session
    useEffect(() => {
        if (!token || (components?.length || socket) || isTerminate) return;

        const fetchReplaySession = async () => {
            try {
                const response = await getReplaySession();
                const {messages = []} = response.data;

                const startButton = messages.find(
                    item => item?.data?.interface_block_type === 'on_start_widget_block'
                );

                const buttonInput = messages.find(
                    item => item.data.message?.interaction_type === 'BUTTON_INPUT'
                );

                if (buttonInput) {
                    dispatch(setButtonInputHint(buttonInput));
                }
                if (startButton) {
                    dispatch(setStartInterfaceBlockID(startButton?.data?.interface_block_id));
                }

                const {traceData, componentData} = processMessages(messages, dispatch);
                const filteredComponentData = componentData.filter(
                    (component) =>
                        !(
                            (component?.data?.message?.message_type === "HINT" && component?.data?.message?.interaction_type === "START_BUTTON") ||
                            (component?.data?.message?.element_type === "SUCCESS_MESSAGE" && component?.data?.interface_block_type === "send_web_success_message_block") ||
                            (component?.data?.message?.element_type === "FAILURE_MESSAGE" && component?.data?.interface_block_type === "send_web_failure_message_block")
                        )
                );

                dispatch(setReplayComponents(response.data));
                dispatch(setTraceData(traceData));
                dispatch(setComponentData(filteredComponentData));
            } catch (error) {
                console.error("Error fetching replay session:", error);
            } finally {
                dispatch(setLoader(false));
            }
        };

        setTimeout(() => {
            if (!isComponentData.current) {
                fetchReplaySession();
            }
        }, 1000)
        //


    }, [components, token, isTerminate, socket]);


    useEffect(() => {
        if (traceData?.length && socket) {
            const isCompleted = traceData[traceData?.length - 1]?.data?.message?.block_current_status === 'COMPLETED';
            if (isCompleted && isTerminate) {
                socket.close();
            }
        }
    }, [step, components, traceData, isTerminate, socket]);

    if (error) {
        return (
            <Suspense fallback={<LoadingFallback/>}>
                <div className="max-w-lg sm:w-full bg-white border mx-auto sm:py-0 py-0 h-full">
                    <Error/>
                </div>
            </Suspense>
        )
    }

    return (
        <div className="max-w-lg sm:w-full bg-white border mx-auto sm:py-0 py-0 h-full">
            <Suspense fallback={<LoadingFallback/>}>
                {loader || (!components?.length && !isTerminate) ? (
                    <LoadingScreen/>
                ) : (
                    <ProductionItem/>
                )}
            </Suspense>
        </div>
    );
}

// Helper functions
const processMessages = (messages, dispatch) => {

    const traceData = [];
    const componentData = [];

    messages.forEach((item) => {
        const {data: {message, interface_block_type} = {}} = item;

        if (message?.message_type === "TRACE") {
            if (
                !["INITIALIZED", "SUSPENDED", "RESCUED"].includes(message?.block_current_status) &&
                interface_block_type !== "router_block"
            ) {
                traceData.push(item);
            }
        } else if (message?.message_type === MESSAGE_TYPES.TERMINATE) {
            dispatch(setIsTerminate(true))
        } else if (message?.element_type !== "WAIT_MESSAGE" && message?.message_type !== 'HALT' && message?.interaction_type !== INTERACTION_TYPE.BUTTON_INPUT) {
            componentData.push(item);
        }
    });

    return {traceData, componentData};
};


export default App;
export {LoadingScreen};